// @flow

/* eslint-disable ta-code-conventions/has-matching-storydoc */
/* eslint-disable ta-code-conventions/has-matching-test */
import * as React from 'react';
import { Query } from 'urql';
import uniqBy from 'lodash/uniqBy';
import { getMediaFilterToRetrieveAllPhotos } from '@ta/media.common';
import AccumulatingProvider from '@ta/common.accumulating-provider';
import { type Photo } from './PhotoCarousel';
import LocationPhotosQuery from './LocationPhotosQuery.gql';

type CallbackArgs = {|
  photos: Array<Photo>,
  photoCount: number,
  getNextPage: () => void,
|};

type Props = {|
  locationId: number,
  children: CallbackArgs => React.Node,
  pageSize: number,
|};

type State = {|
  currentIndex: number,
|};

/**
 * This Provider fetches pages all ordered photos for a location, starting with hero image.
 */
class LocationPhotoCarouselPagingProvider extends React.Component<Props, State> {
  state = {
    currentIndex: 0,
  };

  getPhotos = (photosData: any, addHero: boolean) => {
    let photos = [];
    if (photos.length < this.props.pageSize && photosData.photos) {
      photos.push(...photosData.photos);
    }
    if (addHero) {
      // Start with the hero photo
      if (photosData.thumbnail) {
        photos = [photosData.thumbnail, ...photos.filter(p => p.id !== photosData.thumbnail.id)];
      }
    }
    // Remove duplicates
    photos = uniqBy(photos, 'id');

    return photos || [];
  };

  getOffset = (currentIndex: number) => {
    // Check current index and see if next page (or part of it) falls out of photos
    if (currentIndex >= 1) {
      return currentIndex + 1;
    }
    // Fallback case, return 0;
    return 0;
  };

  getNextPageOnClick = () => {
    this.getNextPage(this.state.currentIndex);
  };

  getNextPage = (currentIndex: number) => {
    const numImages = this.photoCount || 0;

    if (currentIndex < numImages - 1) {
      this.setState({
        currentIndex: currentIndex + this.props.pageSize,
      });
    }
  };

  photoCount: number | null = null;

  render() {
    if (this.props.locationId < 0) {
      return this.props.children({
        photos: [],
        photoCount: 0,
        getNextPage: () => {},
      });
    }
    const photosOffset = this.getOffset(this.state.currentIndex);
    const queryParams = {
      locationId: this.props.locationId,
      plimit: this.props.pageSize,
      poffset: photosOffset,
      filter: getMediaFilterToRetrieveAllPhotos(),
    };

    return (
      <Query query={LocationPhotosQuery} variables={queryParams}>
        {({ fetching, data }) => {
          if (!data || !data.locations || !data.locations[0]) {
            return null;
          }

          if (!fetching && this.photoCount === null) {
            // Cache this so it doesn't disappear during data loads
            this.photoCount = data.locations[0].photoCount;
          }

          return (
            <AccumulatingProvider
              data={
                fetching || !data.locations || data.locations.length === 0
                  ? []
                  : this.getPhotos(data.locations[0], photosOffset === 0)
              }
              identifier={photo => String(photo.id)}
            >
              {photos =>
                this.props.children({
                  photos,
                  photoCount: this.photoCount || 0,
                  getNextPage: fetching ? () => {} : this.getNextPageOnClick,
                })
              }
            </AccumulatingProvider>
          );
        }}
      </Query>
    );
  }
}

export default LocationPhotoCarouselPagingProvider;
